import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { Spinner, ToggleField } from '../../../shared/components';
import {
  FacebookMessengerTestSendResult,
  FacebookMessengerWebhookSubscription,
  UpdateFacebookMessengerSettingsRequest,
} from '../../companies/types/Company';
import {
  subscribeFacebookMessengerWebhooks,
} from '../../companies/services/companyService';
import {
  createMessengerChannelAccount,
  getMessengerChannelAccountSettings,
  sendMessengerChannelAccountTestMessage,
  updateMessengerChannelAccountCredentials,
} from '../services/channelAccountService';
import { ChannelAccountMessengerSettings } from '../types/channelAccount';
import { useChannelAccounts } from '../hooks/useChannelAccounts';
import { ChannelAccountBar } from './ChannelAccountBar';
import { AddMessengerAccountForm } from './AddMessengerAccountForm';
import { ConnectorFieldStatus, ConnectorSubscribedFields } from './ConnectorFieldStatus';

const emptyMessengerSettingsForm: UpdateFacebookMessengerSettingsRequest = {
  apiBaseUrl: 'https://graph.facebook.com',
  apiVersion: 'v25.0',
  pageId: '',
  pageAccessToken: '',
  webhookVerifyToken: '',
  appSecret: '',
  validateSignature: false,
};

const formatDateTime = (value?: string): string => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

interface MessengerConnectorTabProps {
  canManage: boolean;
}

export const MessengerConnectorTab: React.FC<MessengerConnectorTabProps> = ({ canManage }) => {
  const { t } = useTranslation(['companies', 'admin']);
  const { showToast } = useToast();
  const messengerChannelAccounts = useChannelAccounts('Messenger', canManage);

  const [loadingSettings, setLoadingSettings] = React.useState(false);
  const [savingSettings, setSavingSettings] = React.useState(false);
  const [subscribingWebhooks, setSubscribingWebhooks] = React.useState(false);
  const [testingSend, setTestingSend] = React.useState(false);
  const [accountSettings, setAccountSettings] = React.useState<ChannelAccountMessengerSettings | null>(null);
  const [settingsForm, setSettingsForm] = React.useState<UpdateFacebookMessengerSettingsRequest>(emptyMessengerSettingsForm);
  const [settingsError, setSettingsError] = React.useState<string | null>(null);
  const [settingsNotice, setSettingsNotice] = React.useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = React.useState('');
  const [latestTestSendResult, setLatestTestSendResult] = React.useState<FacebookMessengerTestSendResult | null>(null);
  const [latestSubscriptionResult, setLatestSubscriptionResult] = React.useState<FacebookMessengerWebhookSubscription | null>(null);
  const [testRecipientId, setTestRecipientId] = React.useState('');
  const [testMessage, setTestMessage] = React.useState('Hello from ShahAgent Messenger test');
  const [showAddMessengerAccount, setShowAddMessengerAccount] = React.useState(false);

  const hasSettingsChanges = React.useMemo(() => {
    if (!accountSettings) {
      return Boolean(settingsForm.pageAccessToken?.trim())
        || Boolean(settingsForm.webhookVerifyToken?.trim())
        || Boolean(settingsForm.appSecret?.trim())
        || Boolean(settingsForm.pageId?.trim())
        || (settingsForm.apiBaseUrl?.trim() || 'https://graph.facebook.com') !== 'https://graph.facebook.com'
        || (settingsForm.apiVersion?.trim() || 'v25.0') !== 'v25.0'
        || Boolean(settingsForm.validateSignature);
    }

    return Boolean(settingsForm.pageAccessToken?.trim())
      || Boolean(settingsForm.webhookVerifyToken?.trim())
      || Boolean(settingsForm.appSecret?.trim())
      || (settingsForm.pageId?.trim() || '') !== (accountSettings.pageId || '')
      || (settingsForm.apiBaseUrl?.trim() || 'https://graph.facebook.com') !== (accountSettings.apiBaseUrl || 'https://graph.facebook.com')
      || (settingsForm.apiVersion?.trim() || 'v25.0') !== (accountSettings.apiVersion || 'v25.0')
      || Boolean(settingsForm.validateSignature) !== accountSettings.validateSignature;
  }, [accountSettings, settingsForm]);

  const loadAccountSettings = React.useCallback(async (accountId: string) => {
    if (!canManage) {
      return;
    }

    setLoadingSettings(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const data = await getMessengerChannelAccountSettings(accountId);
      setAccountSettings(data);
      setLatestTestSendResult(null);
      setLatestSubscriptionResult(null);
      setSettingsForm({
        pageAccessToken: '',
        webhookVerifyToken: '',
        appSecret: '',
        apiBaseUrl: data.apiBaseUrl || 'https://graph.facebook.com',
        apiVersion: data.apiVersion || 'v25.0',
        pageId: data.pageId || '',
        validateSignature: data.validateSignature,
      });

      const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const defaultWebhookUrl = apiBase
        ? `${apiBase}/api/v1/messenger/webhook`
        : `${window.location.origin.replace(/\/$/, '')}/api/v1/messenger/webhook`;
      setWebhookUrl(defaultWebhookUrl);
    } catch (error) {
      setSettingsError(error instanceof Error ? error.message : t('failedToLoadMessengerSettings'));
    } finally {
      setLoadingSettings(false);
    }
  }, [canManage, t]);

  React.useEffect(() => {
    const accountId = messengerChannelAccounts.selectedAccountId;
    if (!accountId) {
      setAccountSettings(null);
      setSettingsForm(emptyMessengerSettingsForm);
      return;
    }

    void loadAccountSettings(accountId);
  }, [loadAccountSettings, messengerChannelAccounts.selectedAccountId]);

  const saveSettings = async () => {
    const selectedAccount = messengerChannelAccounts.selectedAccount;
    const isInitialSetup = messengerChannelAccounts.accounts.length === 0;

    if (!selectedAccount && !isInitialSetup) {
      return;
    }

    if (isInitialSetup && (!settingsForm.pageId?.trim() || !settingsForm.pageAccessToken?.trim())) {
      const message = t('messengerPageCredentialsRequired', { defaultValue: 'Page ID and page access token are required.' });
      setSettingsError(message);
      showToast(message, 'error');
      return;
    }

    setSavingSettings(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      if (isInitialSetup) {
        await createMessengerChannelAccount({
          displayName: 'Messenger',
          pageId: settingsForm.pageId?.trim() || '',
          pageAccessToken: settingsForm.pageAccessToken?.trim() || '',
          apiBaseUrl: settingsForm.apiBaseUrl?.trim() || undefined,
          apiVersion: settingsForm.apiVersion?.trim() || undefined,
          webhookVerifyToken: settingsForm.webhookVerifyToken?.trim() || undefined,
          appSecret: settingsForm.appSecret?.trim() || undefined,
          validateSignature: settingsForm.validateSignature,
          isDefault: true,
        });
        await messengerChannelAccounts.reload();
      } else if (selectedAccount) {
        await updateMessengerChannelAccountCredentials(selectedAccount.id, {
          apiBaseUrl: settingsForm.apiBaseUrl?.trim() || undefined,
          apiVersion: settingsForm.apiVersion?.trim() || undefined,
          pageId: settingsForm.pageId?.trim() || undefined,
          pageAccessToken: settingsForm.pageAccessToken?.trim() || undefined,
          webhookVerifyToken: settingsForm.webhookVerifyToken?.trim() || undefined,
          appSecret: settingsForm.appSecret?.trim() || undefined,
          validateSignature: settingsForm.validateSignature,
        });
        await loadAccountSettings(selectedAccount.id);
        await messengerChannelAccounts.reload();
      }

      setLatestTestSendResult(null);
      setSettingsForm(prev => ({
        ...prev,
        pageAccessToken: '',
        webhookVerifyToken: '',
        appSecret: '',
      }));
      setSettingsNotice(t('messengerSettingsSaved'));
      showToast(t('messengerSettingsSaved'), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSaveMessengerSettings');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const subscribeWebhooks = async () => {
    setSubscribingWebhooks(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const result = await subscribeFacebookMessengerWebhooks();
      setLatestSubscriptionResult(result);
      setSettingsNotice(t('messengerWebhooksSubscribed', { pageId: result.pageId }));
      showToast(t('messengerWebhooksSubscribedToast', {
        pageId: result.pageId,
        subscribedFields: result.subscribedFields,
        subscribedAt: formatDateTime(result.subscribedAtUtc),
      }), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSubscribeMessengerWebhooks');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setSubscribingWebhooks(false);
    }
  };

  const sendTestMessage = async () => {
    const selectedAccount = messengerChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    if (!testRecipientId.trim()) {
      const message = t('messengerTestRecipientRequired');
      setSettingsError(message);
      setSettingsNotice(null);
      showToast(message, 'error');
      return;
    }

    if (!testMessage.trim()) {
      const message = t('messengerTestMessageRequired');
      setSettingsError(message);
      setSettingsNotice(null);
      showToast(message, 'error');
      return;
    }

    setTestingSend(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const result = await sendMessengerChannelAccountTestMessage(selectedAccount.id, {
        recipientId: testRecipientId.trim(),
        text: testMessage.trim(),
      });
      setLatestTestSendResult(result);
      setSettingsNotice(t('messengerTestSendSucceeded', { recipientId: result.recipientId }));
      showToast(t('messengerTestSendSucceededToast', {
        recipientId: result.recipientId,
        messageId: result.messageId,
        sentAt: formatDateTime(result.sentAtUtc),
      }), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSendMessengerTestMessage');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setTestingSend(false);
    }
  };

  const renderSettingsForm = () => (
    <>
      <div className="company-form-grid">
        <label>
          {t('messengerApiBaseUrl')}
          <input
            className="text-input"
            value={settingsForm.apiBaseUrl || ''}
            onChange={event => setSettingsForm(prev => ({ ...prev, apiBaseUrl: event.target.value }))}
            placeholder="https://graph.facebook.com"
          />
        </label>

        <label>
          {t('messengerApiVersion')}
          <input
            className="text-input"
            value={settingsForm.apiVersion || ''}
            onChange={event => setSettingsForm(prev => ({ ...prev, apiVersion: event.target.value }))}
            placeholder="v25.0"
          />
        </label>

        <label>
          {t('messengerPageId')}
          <input
            className="text-input"
            value={settingsForm.pageId || ''}
            onChange={event => setSettingsForm(prev => ({ ...prev, pageId: event.target.value }))}
            placeholder="123456789012345"
          />
        </label>

        <label>
          {t('messengerPageAccessToken')}
          <input
            className="text-input"
            type="password"
            value={settingsForm.pageAccessToken || ''}
            onChange={event => setSettingsForm(prev => ({ ...prev, pageAccessToken: event.target.value }))}
            placeholder={accountSettings?.hasPageAccessToken ? t('messengerPageAccessTokenConfigured') : t('messengerPageAccessTokenPlaceholder')}
            autoComplete="new-password"
          />
        </label>

        <label>
          {t('messengerWebhookVerifyToken')}
          <input
            className="text-input"
            type="password"
            value={settingsForm.webhookVerifyToken || ''}
            onChange={event => setSettingsForm(prev => ({ ...prev, webhookVerifyToken: event.target.value }))}
            placeholder={accountSettings?.hasWebhookVerifyToken ? t('messengerWebhookVerifyTokenConfigured') : t('messengerWebhookVerifyTokenPlaceholder')}
            autoComplete="new-password"
          />
        </label>

        <label>
          {t('messengerAppSecret')}
          <input
            className="text-input"
            type="password"
            value={settingsForm.appSecret || ''}
            onChange={event => setSettingsForm(prev => ({ ...prev, appSecret: event.target.value }))}
            placeholder={accountSettings?.hasAppSecret ? t('messengerAppSecretConfigured') : t('messengerAppSecretPlaceholder')}
            autoComplete="new-password"
          />
        </label>

        <ToggleField
          label={t('messengerValidateSignature')}
          checked={Boolean(settingsForm.validateSignature)}
          onChange={value => setSettingsForm(prev => ({ ...prev, validateSignature: value }))}
        />
      </div>

      <div style={{ display: 'grid', gap: 6, marginTop: 12, color: '#516173', fontSize: 13 }}>
        <div>{t('messengerPageAccessTokenHint')}</div>
        <div>{t('messengerWebhookVerifyTokenHint')}</div>
        <div>{t('messengerAppSecretHint')}</div>
        {accountSettings ? (
          <div>{t('messengerSettingsStatus', {
            pageAccessToken: accountSettings.hasPageAccessToken ? t('configured') : t('missing'),
            verifyToken: accountSettings.hasWebhookVerifyToken ? t('configured') : t('missing'),
            appSecret: accountSettings.hasAppSecret ? t('configured') : t('missing'),
          })}</div>
        ) : null}
      </div>

      {accountSettings ? (
        <div className="telegram-status-grid">
          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('messengerStatusCredentials')}</div>
            <dl className="telegram-status-list">
              <div>
                <dt>{t('messengerStatusApiBaseUrl')}</dt>
                <dd>{accountSettings.apiBaseUrl || settingsForm.apiBaseUrl || t('telegramStatusNotAvailable')}</dd>
              </div>
              <div>
                <dt>{t('messengerStatusApiVersion')}</dt>
                <dd>{accountSettings.apiVersion || settingsForm.apiVersion || t('telegramStatusNotAvailable')}</dd>
              </div>
              <div>
                <dt>{t('messengerStatusPageId')}</dt>
                <dd>{accountSettings.pageId || settingsForm.pageId || t('telegramStatusNotAvailable')}</dd>
              </div>
              <div>
                <dt>{t('messengerStatusPageAccessToken')}</dt>
                <dd><ConnectorFieldStatus done={Boolean(accountSettings.hasPageAccessToken)} /></dd>
              </div>
              <div>
                <dt>{t('messengerAppSecret')}</dt>
                <dd><ConnectorFieldStatus done={Boolean(accountSettings.hasAppSecret)} /></dd>
              </div>
            </dl>
          </div>

          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('messengerStatusWebhook')}</div>
            <dl className="telegram-status-list">
              <div>
                <dt>{t('messengerStatusWebhookUrl')}</dt>
                <dd>{webhookUrl || t('telegramStatusNotAvailable')}</dd>
              </div>
              <div>
                <dt>{t('messengerStatusVerifyToken')}</dt>
                <dd><ConnectorFieldStatus done={Boolean(accountSettings.hasWebhookVerifyToken)} /></dd>
              </div>
              <div>
                <dt>{t('messengerStatusSignatureValidation')}</dt>
                <dd><ConnectorFieldStatus done={Boolean(accountSettings.validateSignature)} variant="enabled" /></dd>
              </div>
              {latestSubscriptionResult ? (
                <div>
                  <dt>{t('messengerStatusSubscribedFields')}</dt>
                  <dd><ConnectorSubscribedFields fields={latestSubscriptionResult.subscribedFields} /></dd>
                </div>
              ) : null}
            </dl>
          </div>

          <div className="telegram-status-card">
            <div className="telegram-status-card-title">{t('messengerStatusTestSend')}</div>
            {latestTestSendResult ? (
              <dl className="telegram-status-list">
                <div>
                  <dt>{t('messengerStatusRecipientId')}</dt>
                  <dd>{latestTestSendResult.recipientId}</dd>
                </div>
                <div>
                  <dt>{t('messengerStatusMessageId')}</dt>
                  <dd>{latestTestSendResult.messageId}</dd>
                </div>
                <div>
                  <dt>{t('telegramStatusSentAt')}</dt>
                  <dd>{formatDateTime(latestTestSendResult.sentAtUtc)}</dd>
                </div>
              </dl>
            ) : (
              <div className="telegram-status-empty">{t('messengerStatusNoTestSend')}</div>
            )}
          </div>
        </div>
      ) : null}

      <div className="company-actions-inline" style={{ marginTop: 16 }}>
        <button type="button" className="btn btn-primary" onClick={saveSettings} disabled={savingSettings || !hasSettingsChanges}>
          {savingSettings ? t('savingEllipsis', { ns: 'admin' }) : t('saveMessengerSettings')}
        </button>
        {accountSettings ? (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={subscribeWebhooks}
            disabled={subscribingWebhooks || !accountSettings.hasPageAccessToken}
          >
            {subscribingWebhooks ? t('savingEllipsis', { ns: 'admin' }) : t('subscribeMessengerWebhooks')}
          </button>
        ) : null}
        {accountSettings ? (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setSettingsForm({
                pageAccessToken: '',
                webhookVerifyToken: '',
                appSecret: '',
                apiBaseUrl: accountSettings.apiBaseUrl || 'https://graph.facebook.com',
                apiVersion: accountSettings.apiVersion || 'v25.0',
                pageId: accountSettings.pageId || '',
                validateSignature: accountSettings.validateSignature ?? false,
              });
              setSettingsError(null);
              setSettingsNotice(null);
            }}
            disabled={savingSettings}
          >
            {t('resetMessengerForm')}
          </button>
        ) : null}
      </div>

      {accountSettings ? (
        <div className="company-section" style={{ marginTop: 16 }}>
          <div className="company-section-head">
            <h3>{t('messengerTestSendSection')}</h3>
          </div>
          <div className="company-form-grid">
            <label>
              {t('messengerTestRecipientId')}
              <input
                className="text-input"
                value={testRecipientId}
                onChange={event => setTestRecipientId(event.target.value)}
                placeholder="PSID from a customer message"
              />
            </label>
            <label>
              {t('messengerTestMessage')}
              <input
                className="text-input"
                value={testMessage}
                onChange={event => setTestMessage(event.target.value)}
              />
            </label>
          </div>
          <div style={{ color: '#516173', fontSize: 13, marginTop: 8 }}>
            {t('messengerTestSendHint')}
          </div>
          <div className="company-actions-inline" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={sendTestMessage}
              disabled={testingSend || !testRecipientId.trim() || !testMessage.trim()}
            >
              {testingSend ? t('savingEllipsis', { ns: 'admin' }) : t('sendMessengerTestMessage')}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );

  if (!canManage) {
    return null;
  }

  return (
    <section className="company-section">
      <div className="company-section-head">
        <h3>{t('messengerSettingsHeading')}</h3>
      </div>

      <p style={{ color: '#516173', marginTop: 0 }}>
        {t('messengerSettingsDescription')}
      </p>

      {settingsError ? <div className="error">{settingsError}</div> : null}
      {settingsNotice ? <div className="success">{settingsNotice}</div> : null}

      <ChannelAccountBar
        channel="Messenger"
        accounts={messengerChannelAccounts.accounts}
        limits={messengerChannelAccounts.limits}
        loading={messengerChannelAccounts.loading}
        error={messengerChannelAccounts.error}
        selectedAccountId={messengerChannelAccounts.selectedAccountId}
        onSelectAccount={messengerChannelAccounts.setSelectedAccountId}
        onRefresh={messengerChannelAccounts.reload}
        canManage={canManage}
        showAddForm={showAddMessengerAccount}
        onToggleAddForm={() => setShowAddMessengerAccount(previous => !previous)}
        onAccountUpdated={() => {
          const accountId = messengerChannelAccounts.selectedAccountId;
          if (accountId) {
            void loadAccountSettings(accountId);
          }
        }}
      />

      {showAddMessengerAccount ? (
        <AddMessengerAccountForm
          canManage={canManage}
          onCancel={() => setShowAddMessengerAccount(false)}
          onCreated={() => {
            setShowAddMessengerAccount(false);
            void messengerChannelAccounts.reload();
            showToast(t('channelAccountCreated', { ns: 'companies' }), 'success');
          }}
        />
      ) : null}

      {messengerChannelAccounts.accounts.length === 0 ? (
        <>
          <p style={{ color: '#516173', marginTop: 16 }}>
            {t('channelAccountCreateAccountForSettings', { ns: 'companies' })}
          </p>
          {renderSettingsForm()}
        </>
      ) : messengerChannelAccounts.selectedAccountId ? (
        loadingSettings ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
            <Spinner text={t('loadingMessengerSettings')} />
          </div>
        ) : renderSettingsForm()
      ) : null}
    </section>
  );
};
